/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import { Control, ControlError, Input, Label } from "@forge/common";
import TextBoxComponentForSettingProps from "../../../models/componentModels/TextBoxComponentForSettingProps";
import SettingsFieldComponentProps from "../../../models/componentModels/SettingsFieldComponentProps";
import { get } from "node:https";
import { useRef, useState, useEffect } from "react";
import { set } from "zod";
import { useDispatch } from "react-redux";
import { tenantActions } from "../../../store/Tenant/tenant-slice";
import { localeKeyMap } from "../../../localization/keys";
import { useTranslation } from "@scuf/localization";
import { isEmptyField } from "./validators/isEmptyField";

const TextBoxComponent = ({
  setting,
  newSettings,
  onInputChange,
  productId,
  schema,
  settingKeyValidation,
}: TextBoxComponentForSettingProps) => {
  const [textValue, setTextValue] = useState(
    newSettings?.find((x: { key: any }) => x.key === setting.key)?.value ?? ""
  );
  const errorMessage = useRef(
    newSettings?.find((x: { key: any }) => x.key === setting.key)
      ?.errorMessage ?? ""
  );
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const isRequired = setting?.required === true;

  // Helper function to handle empty field validation
  const handleEmptyFieldValidation = (value: any) => {
    if (isRequired && isEmptyField(value)) {
      errorMessage.current = t(localeKeyMap.requiredField);
    } else {
      // Clear error message if field has value (unless validation sets a different error)
      if (errorMessage.current === t(localeKeyMap.requiredField)) {
        errorMessage.current = "";
      }
    }
  };

  // Update errorMessage when newSettings changes
  useEffect(() => {
    const currentSetting = newSettings?.find((x: { key: any }) => x.key === setting.key);
    if (currentSetting) {
      errorMessage.current = currentSetting.errorMessage ?? "";
    }
  }, [newSettings, setting.key]);

  // Function to get the type of the text box
  const getTypeOfTextBox = (settingType: SettingsFieldComponentProps) => {
    switch (settingType) {
      case "string":
        return "text";
      case "number":
        return "number";
      case "textbox":
        return "text";
      case "secret":
        return "password";
      default:
        return "text";
    }
  };

  return (
    <Control>
      <Label htmlFor={setting?.key} required={isRequired}>
        {setting?.displayName}
      </Label>
      <Input
        {...settingKeyValidation}
        error={Boolean(!(getErrorMessages(newSettings, setting) === ""))}
        required={isRequired}
        id={setting?.key}
        className="settings-input"
        placeholder={setting?.displayName}
        size="regular"
        type={getTypeOfTextBox(setting?.type)}
        autoComplete={"off"}
        value={textValue}
        onBlur={() => {
          // Handle empty field validation
          handleEmptyFieldValidation(textValue);
          
          const newEvent = {
            target: {
              value: textValue,
            },
          };
          onInputChange(newEvent, setting, productId, errorMessage.current);
        }}
        onFocus={(event: any) => {
          const value = textValue;
          const settingType = setting.type;
          let updatedEventForSecret = {
            ...event,
            target: { ...event?.target, value },
          };
          if (settingType === "secret" && value === "*****") {
            // Clear the stars to allow user to enter new secret value
            setTextValue("");
          }
        }}
        onChange={(event: any) => {
          errorMessage.current =
            !isRequired && event.target.value === ""
              ? ""
              : settingKeyValidation.onChange(event);

          setTextValue(event.target.value);

          // Calculate the error count and update the store
          let count = 0;
          
          // Handle empty field validation
          handleEmptyFieldValidation(event.target.value);
          
          newSettings.forEach((othersetting: any) => {
            if (othersetting && othersetting.key === setting.key) {
              if (errorMessage.current?.length > 0) {
                count++;
              }
            } else if (othersetting && othersetting?.errorMessage?.length > 0) {
              count++;
            }
          });

          const tabAndError = {
            tabName: productId,
            errorCount: count,
          };

          // Update the error count in the store
          dispatch(
            tenantActions.updateTabErrorCountMapping(tabAndError as any)
          );
        }}
        //maxLength={256}
        {...(errorMessage.current && {
          error: true,
        })}
      />
      {errorMessage.current?.length > 0 && (
        <ControlError>{errorMessage.current}</ControlError>
      )}
    </Control>
  );
};

export const getErrorMessages = (newSettings: any, setting: any) => {
  const errorMessage =
    newSettings?.find((x: { key: any }) => x?.key === setting?.key)
      ?.errorMessage ?? "";

  return errorMessage;
};

export default TextBoxComponent;
